import { useState } from 'react';
import { ArrowRightLeft, Calendar, Users, Plane } from 'lucide-react';

// QUOTE — quote-terminal layout: trip-builder card on the left, summary "ticket" on the right.
// Distinctive: looks like an ATC strip / flight booking terminal.
export default function Quote() {
  const [from, setFrom] = useState('TEB');
  const [to, setTo] = useState('LFPB');
  const [pax, setPax] = useState(8);
  const [cls, setCls] = useState('Mid (Falcon 7X)');
  const rates = { 'Light (Phenom 300E)': 4800, 'Mid (Falcon 7X)': 8400, 'Heavy (Gulfstream G650ER)': 12400 };
  const hours = 7.4;
  const subtotal = (rates[cls] * hours).toFixed(0);

  return (
    <div className="bg-[#0F0F11] text-[#EAF1F4]">
      <section className="pt-14 pb-10 max-w-[1500px] mx-auto px-6 lg:px-10 border-b border-[#EAF1F4]/10">
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#D73B26] mb-4">⏵ Section 04 / Quote Terminal</p>
        <h1 className="font-serif text-5xl lg:text-8xl leading-[0.92] tracking-[-0.025em] max-w-5xl">Build a leg. <span className="italic text-[#D73B26]">Get a number</span>.</h1>
      </section>

      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-[1.4fr_1fr] gap-10 items-start">
        {/* Trip builder */}
        <form className="border border-[#EAF1F4]/15 bg-[#06060A] p-8 lg:p-10 space-y-8" onSubmit={(e) => e.preventDefault()}>
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#D73B26] mb-2">/ Routing</p>
            <div className="grid grid-cols-[1fr_auto_1fr] gap-4 items-center">
              <FieldInline label="ICAO from" value={from} onChange={setFrom} testId="jet-quote-from" />
              <ArrowRightLeft className="w-5 h-5 text-[#EAF1F4]/40 mt-7" strokeWidth={1.4} />
              <FieldInline label="ICAO to" value={to} onChange={setTo} testId="jet-quote-to" />
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Departure" type="date" testId="jet-quote-date" icon={<Calendar className="w-3.5 h-3.5" strokeWidth={1.4} />} />
            <Field label="Pax" type="number" value={pax} onChange={setPax} testId="jet-quote-pax" icon={<Users className="w-3.5 h-3.5" strokeWidth={1.4} />} />
            <SelectField label="Class" value={cls} onChange={setCls} testId="jet-quote-class" options={Object.keys(rates)} icon={<Plane className="w-3.5 h-3.5" strokeWidth={1.4} />} />
          </div>
          <div className="border-t border-[#EAF1F4]/10 pt-8">
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#D73B26] mb-2">/ Contact</p>
            <div className="grid sm:grid-cols-2 gap-4 mt-3">
              <PlainField label="Name" placeholder="Your name" testId="jet-quote-name" />
              <PlainField label="Telephone" type="tel" placeholder="+44…" testId="jet-quote-tel" />
            </div>
            <PlainField label="Email" type="email" placeholder="you@domain.com" testId="jet-quote-email" />
          </div>
          <button type="button" data-testid="jet-quote-submit" className="bg-[#D73B26] text-[#0F0F11] px-8 py-4 font-mono text-[10px] tracking-[0.32em] uppercase hover:bg-[#EAF1F4] transition-colors w-full">Send to Operations →</button>
          <p className="font-mono text-[9px] tracking-[0.25em] uppercase text-[#EAF1F4]/40">Median response: 14 minutes</p>
        </form>

        {/* "Ticket" summary — boarding-pass style */}
        <aside className="lg:sticky lg:top-32 border border-[#EAF1F4]/15 bg-[#06060A] overflow-hidden">
          <div className="px-6 py-3 border-b border-[#EAF1F4]/10 bg-[#0F0F11] flex items-center justify-between font-mono text-[9px] tracking-[0.3em] uppercase">
            <span className="text-[#D73B26]">/ Estimate · STR-Q-2026</span>
            <span className="text-[#EAF1F4]/45">DRAFT</span>
          </div>
          <div className="p-8">
            <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center">
              <div>
                <p className="font-serif text-5xl">{from}</p>
                <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-[#EAF1F4]/45 mt-1">From</p>
              </div>
              <div className="border-t-2 border-dashed border-[#D73B26]/40 relative h-px"><Plane className="w-4 h-4 text-[#D73B26] absolute left-1/2 -translate-x-1/2 -top-2" strokeWidth={1.4} /></div>
              <div className="text-right">
                <p className="font-serif text-5xl">{to}</p>
                <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-[#EAF1F4]/45 mt-1">To</p>
              </div>
            </div>
            <table className="w-full font-mono text-[11px] mt-8 border-t border-[#EAF1F4]/10">
              <tbody>
                {[['Aircraft', cls], ['Pax', pax], ['Block hours', hours.toFixed(1)], ['Hourly', '£' + rates[cls].toLocaleString()]].map(([k, v]) => (
                  <tr key={k} className="border-b border-[#EAF1F4]/10"><td className="py-2.5 text-[#EAF1F4]/45 tracking-[0.2em] uppercase">{k}</td><td className="py-2.5 text-right">{v}</td></tr>
                ))}
              </tbody>
            </table>
            <div className="border-t-2 border-dashed border-[#EAF1F4]/15 mt-6 pt-6 flex items-end justify-between">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#EAF1F4]/45">Subtotal</p>
              <p className="font-serif text-4xl text-[#D73B26]" data-testid="jet-quote-subtotal">£{Number(subtotal).toLocaleString()}</p>
            </div>
            <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#EAF1F4]/40 mt-2 leading-relaxed">Estimate. Firm price issued by Operations within 14 min of submission.</p>
          </div>
        </aside>
      </section>
    </div>
  );
}

function FieldInline({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#EAF1F4]/45 block mb-2">{label}</label>
      <input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value.toUpperCase())} maxLength={4}
        className="w-full bg-[#0F0F11] border border-[#EAF1F4]/15 px-4 py-3 outline-none focus:border-[#D73B26] font-serif text-3xl uppercase tracking-tight" />
    </div>
  );
}
function Field({ label, value, onChange, type = 'text', testId, icon }) {
  return (
    <div>
      <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#EAF1F4]/45 mb-2 flex items-center gap-2">{icon}{label}</label>
      <input type={type} data-testid={testId} value={value} onChange={(e) => onChange && onChange(e.target.value)}
        className="w-full bg-[#0F0F11] border border-[#EAF1F4]/15 px-4 py-3 outline-none focus:border-[#D73B26] text-base text-[#EAF1F4]" />
    </div>
  );
}
function SelectField({ label, value, onChange, options, testId, icon }) {
  return (
    <div>
      <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#EAF1F4]/45 mb-2 flex items-center gap-2">{icon}{label}</label>
      <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-[#0F0F11] border border-[#EAF1F4]/15 px-4 py-3 outline-none focus:border-[#D73B26] text-sm text-[#EAF1F4]">
        {options.map(o => <option key={o}>{o}</option>)}
      </select>
    </div>
  );
}
function PlainField({ label, type = 'text', placeholder, testId }) {
  return (
    <div className="mt-3">
      <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#EAF1F4]/45 block mb-2">{label}</label>
      <input type={type} placeholder={placeholder} data-testid={testId} className="w-full bg-[#0F0F11] border border-[#EAF1F4]/15 px-4 py-3 outline-none focus:border-[#D73B26] text-base text-[#EAF1F4] placeholder:text-[#EAF1F4]/30" />
    </div>
  );
}
